import { ArrowUpRight } from 'lucide-react'
import type { Game, GameAccent } from '@/data/games'
import { useLanguage } from '@/localization'

/** Accent-specific styling tokens. */
const accentMap: Record<
  GameAccent,
  { dot: string; cta: string; badge: string }
> = {
  red: {
    dot: 'bg-uliunai-red',
    cta: 'bg-uliunai-red hover:bg-uliunai-red-dark text-white',
    badge: 'bg-uliunai-red/90 text-white ring-white/25',
  },
  green: {
    dot: 'bg-green-500',
    cta: 'bg-green-600 hover:bg-green-700 text-white',
    badge: 'bg-green-600/90 text-white ring-white/25',
  },
  zinc: {
    dot: 'bg-stone-400',
    cta: 'bg-stone-200 text-stone-500',
    badge: 'bg-stone-700/85 text-white ring-white/25',
  },
}

/**
 * A single featured game card. Any card with an `href` links out — live games
 * to their running site, upcoming games to their teaser/preview site. Upcoming
 * cards without an href show a non-clickable coming-soon CTA.
 */
export default function GameCard({ game }: { game: Game }) {
  const { t } = useLanguage()
  const accent = accentMap[game.accent]
  const isLive = game.status === 'live'
  const clickable = !!game.href
  const statusLabel = isLive ? t('Live now') : t('Coming soon')

  // Lift via `top` (main-thread paint), NOT `transform`. A transform transition
  // promotes the card to a GPU layer mid-animation, which makes the 1px rounded
  // border render crisply for the lift's duration and soft again once it settles
  // — a visible border "flash". Animating `top` keeps the card off the
  // compositor, so the border renders consistently throughout.
  const inner = (
    <div
      className="group relative top-0 flex h-full flex-col overflow-hidden rounded-2xl border border-stone-line bg-white shadow-card transition-[top,box-shadow] duration-300 hover:-top-1.5 hover:shadow-card-hover"
    >
      {/* Image */}
      <div className="relative aspect-[16/10] overflow-hidden">
        <img
          src={game.image}
          alt={game.name}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        {/* overlay — fade image bottom into the white card body */}
        <div className="absolute inset-0 bg-gradient-to-t from-white via-white/20 to-transparent" />

        {/* status badge */}
        {game.badge && (
          <div className="absolute left-4 top-4 flex flex-wrap gap-1.5">
            <span
              className={`inline-flex items-center rounded-full px-3 py-1 font-rajdhani text-xs font-semibold uppercase tracking-wider ring-1 backdrop-blur ${accent.badge}`}
            >
              {game.badge ? t(game.badge) : null}
            </span>
            {game.secondaryBadge && (
              <span className="inline-flex items-center rounded-full bg-amber-600/90 px-3 py-1 font-rajdhani text-xs font-semibold uppercase tracking-wider text-white ring-1 ring-white/25 backdrop-blur">
                {t(game.secondaryBadge)}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Body */}
      <div className="flex flex-1 flex-col p-6">
        <div className="flex items-center gap-2">
          <span className={`h-2 w-2 rounded-full ${accent.dot}`} />
          <span className="font-rajdhani text-xs font-semibold uppercase tracking-[0.2em] text-stone-500">
            {statusLabel}
          </span>
        </div>

        <h3 className="mt-2 font-orbitron text-2xl font-bold text-stone-900">
          {game.name}
        </h3>
        <p className="mt-1 flex-1 font-rajdhani text-sm text-stone-600">
          {t(game.tagline)}
        </p>

        {/* CTA */}
        <div className="mt-5">
          {clickable ? (
            <span
              className={`inline-flex items-center gap-2 rounded-lg px-5 py-2.5 font-rajdhani text-sm font-bold uppercase tracking-wide transition-colors ${accent.cta}`}
            >
              {t(game.cta)}
              <ArrowUpRight
                size={16}
                className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              />
            </span>
          ) : (
            <span
              className={`inline-flex cursor-not-allowed items-center gap-2 rounded-lg px-5 py-2.5 font-rajdhani text-sm font-bold uppercase tracking-wide ${accent.cta}`}
            >
              {t(game.cta)}
            </span>
          )}
        </div>
      </div>
    </div>
  )

  if (clickable) {
    return (
      <a
        href={game.href}
        aria-label={`${game.name} - ${t(game.cta)}`}
        className="block h-full focus:outline-none focus-visible:ring-2 focus-visible:ring-uliunai-red focus-visible:ring-offset-2 focus-visible:ring-offset-parchment rounded-2xl"
      >
        {inner}
      </a>
    )
  }

  return (
    <div aria-disabled className="h-full select-none">
      {inner}
    </div>
  )
}
